import { useMutation, type UseMutationResult, useQueryClient } from 'react-query';
import { QUERY_KEYS } from '@/utils/queryKeys';
import { captureException } from '@sentry/react';
import { userActions } from '@/containers/App/actions';
import { useDispatch } from 'react-redux';
import { useServices } from '@/components/Services';
import type { Args, Results } from '@/services/api/users';
import { changeUserSettingsSuccess } from '@/routes/user_preferences/actions';
import { useActiveUser } from '@/containers/App';
import { omit } from 'ramda';

export const useUpdateMe = (): UseMutationResult<
  Results.UpdateMeResult,
  unknown,
  Args.UpdateMySettingsArgs | Args.ChangeMyPasswordArgs,
  unknown
> => {
  const activeUser = useActiveUser();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { usersApiClient } = useServices();

  return useMutation({
    mutationFn: async (args: Args.UpdateMySettingsArgs | Args.ChangeMyPasswordArgs) =>
      await usersApiClient.updateMe(args),
    onError: (err, variables) => {
      const { newPassword } = variables ?? {};

      if (newPassword) {
        dispatch(userActions.changePassword.error());
      } else {
        dispatch(userActions.updateMySettings.error());
      }

      console.error(err);
      captureException(err);
    },
    onSuccess: (responseData, variables) => {
      queryClient.invalidateQueries(QUERY_KEYS.users.all());

      // NOTE: If locale is provided, we don't want to show notification
      if ('locale' in variables) return;

      // NOTE: Temporary fix to update redux state of the active user
      if (responseData && activeUser) {
        dispatch(changeUserSettingsSuccess({ ...activeUser, ...omit(['customer'], responseData) }));
      }

      if (variables?.newPassword) {
        dispatch(userActions.changePassword.success());
      } else {
        dispatch(userActions.updateMySettings.success());
      }

      if ('includeVat' in variables) {
        queryClient.invalidateQueries(QUERY_KEYS.analysis.all());
        queryClient.invalidateQueries(QUERY_KEYS.records.all());
        queryClient.invalidateQueries(QUERY_KEYS.supplyPoints.all());
      }
    },
  });
};
